import { useEffect, useRef, useState } from 'react';
import c from 'classnames';

import usePublishedVideoResources from 'services/general/videoResourcesList';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import VideoResource from 'types/VideoResource';
import { Spinfinity } from '@repo/ui';
import { Typography, ImageFallback } from 'components';
import UpgradeModal from './UpgradeModal';
import bgPlaceholder from 'assets/images/microbe-paper.svg';

import styles from './VideoResources.module.scss';

const getYouTubeThumbnail = (videoUrl?: string) => {
  if (!videoUrl) return '';

  // Extract video ID from different YouTube URL formats
  const youtubeRegex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;
  const match = videoUrl.match(youtubeRegex);

  if (!match) return '';

  const videoId = match[1];
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
};

type VideoCardProps = {
  video: VideoResource;
  setSelectedVideo: (video: VideoResource) => void;
  onUpgradeClick: () => void;
};

const Tags = ({ tags }: { tags?: string }) => {
  if (!tags) return <></>;
  return (
    <div className={styles.tags}>
      {tags.split(',').map(tag => (
        <span key={tag} className={styles.tag}>
          <Typography variant='label'>{tag}</Typography>
        </span>
      ))}
    </div>
  );
};

const VideoCard = ({
  video,
  setSelectedVideo,
  onUpgradeClick,
}: VideoCardProps) => {
  return (
    <div
      className={styles.videoCard}
      onClick={() => {
        if (!video.video_url) {
          onUpgradeClick();
          return;
        }
        setSelectedVideo(video);
      }}
    >
      <div className={styles.videoThumbnail}>
        <ImageFallback
          src={video.thumbnail_url}
          fallback={getYouTubeThumbnail(video.video_url) || bgPlaceholder}
        />
        {!video.video_url && (
          <div
            className={styles.overlay}
            onClick={e => {
              e.stopPropagation();
              onUpgradeClick();
            }}
          >
            <svg
              width='29'
              height='29'
              viewBox='0 0 17 22'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M14.25 7.66667H13.2916V5.75C13.2916 3.105 11.145 0.958336 8.49998 0.958336C5.85498 0.958336 3.70831 3.105 3.70831 5.75V7.66667H2.74998C1.69581 7.66667 0.833313 8.52917 0.833313 9.58334V19.1667C0.833313 20.2208 1.69581 21.0833 2.74998 21.0833H14.25C15.3041 21.0833 16.1666 20.2208 16.1666 19.1667V9.58334C16.1666 8.52917 15.3041 7.66667 14.25 7.66667ZM8.49998 16.2917C7.44581 16.2917 6.58331 15.4292 6.58331 14.375C6.58331 13.3208 7.44581 12.4583 8.49998 12.4583C9.55415 12.4583 10.4166 13.3208 10.4166 14.375C10.4166 15.4292 9.55415 16.2917 8.49998 16.2917ZM11.4708 7.66667H5.52915V5.75C5.52915 4.11125 6.86123 2.77917 8.49998 2.77917C10.1387 2.77917 11.4708 4.11125 11.4708 5.75V7.66667Z'
                fill='white'
              />
            </svg>
          </div>
        )}
      </div>
      <div className={styles.videoInfo}>
        <div className={c('hide-on-mobile', styles.forceLineHeight)}>
          <Typography variant='label'>{video.short_title}</Typography>
        </div>
        <div className='hide-on-desktop'>
          <Typography variant='heading-s'>{video.short_title}</Typography>
        </div>
      </div>
    </div>
  );
};

declare namespace YT {
  interface Player {
    loadVideoById: (videoId: string) => void;
    getPlayerState: () => number;
    playVideo: () => void;
    destroy: () => void;
  }

  interface PlayerEvent {
    target: Player;
    data?: any;
  }
}

declare global {
  interface Window {
    YT: {
      Player: new (
        element: HTMLElement | string,
        options: {
          videoId: string;
          host?: string;
          playerVars?: {
            rel?: number;
            controls?: number;
            fs?: number;
            iv_load_policy?: number;
            origin?: string;
            widget_referrer?: string;
            enablejsapi?: number;
            disablekb?: number;
            playsinline?: number;
            autohide?: number;
            playlist?: string;
            loop?: number;
            mute?: number;
          };
          events?: {
            onReady?: (event: YT.PlayerEvent) => void;
            onError?: (event: YT.PlayerEvent) => void;
          };
        },
      ) => YT.Player;
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

export const VideoResources = () => {
  const [selectedVideo, setSelectedVideo] = useState<VideoResource | null>(
    null,
  );
  const { data: videoResources } = usePublishedVideoResources(); //order desc
  const playerInstanceRef = useRef<YT.Player | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();

  // Filter out locked videos (no video_url) if partner wants to hide membership content
  const filteredVideoResources = videoResources?.filter(video => {
    if (
      (portalConfig.hide_upsells || isPortalConfigLoading) &&
      !video.video_url
    ) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (!selectedVideo?.video_url) return;

    const initPlayer = () => {
      const videoId = getVideoId(selectedVideo.video_url);
      if (!videoId) return;

      // Always destroy any existing player before creating a new one
      if (playerInstanceRef.current) {
        console.log(
          '[VideoResources] Destroying existing player before creating new one',
        );
        try {
          playerInstanceRef.current.destroy();
        } catch (error) {
          console.error(
            '[VideoResources] Error destroying existing player',
            error,
          );
        }
        playerInstanceRef.current = null;
      }

      const playerDiv = document.getElementById('youtube-player');
      if (!playerDiv) {
        console.warn('[VideoResources] youtube-player div not found');
        return;
      }

      try {
        const player = new window.YT.Player('youtube-player', {
          videoId,
          playerVars: {
            controls: 1,
            rel: 0,
            iv_load_policy: 3,
            enablejsapi: 1,
            autohide: 1,
            loop: 1,
            mute: selectedVideo?.is_featured ? 1 : 0,
          },
          events: {
            onReady: event => {
              playerInstanceRef.current = event.target;
              try {
                event.target.playVideo();
              } catch (error) {
                console.error(
                  '[VideoResources] Error calling playVideo',
                  error,
                );
              }
            },
            onError: event => {
              console.error('Player error:', event);
            },
          },
        });
      } catch (error) {
        console.error('Error creating player:', error);
      }
    };

    if (window.YT) {
      // Small delay to ensure the div is mounted
      setTimeout(initPlayer, 100);
    } else {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = initPlayer;
    }
  }, [selectedVideo?.video_url]);

  useEffect(() => {
    return () => {
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
        playerInstanceRef.current = null;
      }
    };
  }, []);

  const getVideoId = (url: string) => {
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
    const match = url.match(youtubeRegex);
    return match ? match[1] : '';
  };

  useEffect(() => {
    if (!filteredVideoResources?.length || selectedVideo) {
      return;
    }
    const featuredVideo = filteredVideoResources.find(
      video => video.is_featured,
    );
    if (featuredVideo) {
      setSelectedVideo(featuredVideo);
    } else {
      setSelectedVideo(filteredVideoResources[0]);
    }
  }, [filteredVideoResources, selectedVideo]);

  const videoList = filteredVideoResources?.filter(
    video => video.id !== selectedVideo?.id,
  );

  return (
    <div className='flex flex-column gap-3 mb-4'>
      <Typography variant='heading-xl'>Microbiome Resources</Typography>
      <div className={styles.videosContainer}>
        <div className={styles.featuredVideo}>
          {!selectedVideo?.video_url ? (
            <Spinfinity />
          ) : (
            <>
              <div className={styles.videoEmbed}>
                <div
                  id='youtube-player'
                  style={{ width: '100%', height: '100%', aspectRatio: '16/9' }}
                />
              </div>
              <Typography variant='heading-m'>
                {selectedVideo.full_title}
              </Typography>
              <Tags tags={selectedVideo.tags} />
            </>
          )}
        </div>
        <div className={styles.videoListWrapper}>
          <div className={styles.videoList}>
            {videoList?.map(video => (
              <VideoCard
                key={video.id}
                video={video}
                setSelectedVideo={video => {
                  setSelectedVideo(video);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onUpgradeClick={() => setShowUpgradeModal(true)}
              />
            ))}
          </div>
        </div>
      </div>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};
